'use client';

import config from 'config';
import { ROUTES } from 'constant';
import { useGetSystemPreferencesQuery } from 'services/userProfileApi';

import { StackProps, useBreakpointValue, VStack } from '@chakra-ui/react';

import { useDashboardSectionAccess } from 'hooks';
import { useAppSelector } from 'store/hooks';
import { userSelector } from 'store/reducers/userSlice';

import { canPurchaseGiftCards } from 'utils';

import { PromoBanner } from 'components/PromoBanner';
import { GiftCardBlock, SavingsBlock } from './components';

const {
  dashboard: { sidebar },
  shop: shopFlags
} = config;

type SecondarySidebarProps = {
  /** Chakra display value — lets a page hide the sidebar per breakpoint. */
  display?: StackProps['display'];
};

const SecondarySidebar = ({ display }: SecondarySidebarProps) => {
  const { user } = useAppSelector(userSelector);
  const { data: sysPrefs } = useGetSystemPreferencesQuery('');
  const { isSectionEnabled } = useDashboardSectionAccess();
  const isShowPromoBanner = useBreakpointValue({ base: false, lg: true });
  const canBuyGiftCards = canPurchaseGiftCards(sysPrefs, shopFlags);
  // When the account gift-cards section is off, keep the balance visible but
  // point its link at the storefront instead of the gated `/a/gc` route.
  const giftCardHref = isSectionEnabled('giftCards')
    ? undefined
    : ROUTES.giftCardsShop;

  return (
    <VStack
      gap={0}
      width='100%'
      maxWidth={{ base: '100%', lg: '280px', xl: '304px' }}
      pl={0}
      display={display}
    >
      {user?.membership_bank_balance && (
        <SavingsBlock
          title='Membership Bank'
          amount={user?.membership_bank_balance}
        />
      )}

      <GiftCardBlock
        title={
          user?.gift_cards_balance
            ? sidebar.availableGiftCardsText
            : sidebar.noGiftCardsText
        }
        titleFontSize={user?.gift_cards_balance ? '11px' : '9px'}
        amount={
          user?.gift_cards_balance || (canBuyGiftCards ? 'Buy Gift Card' : '')
        }
        image={sidebar.giftCard}
        isAlternativeVariant={!!user?.membership_bank_balance}
        href={giftCardHref}
      />

      {isShowPromoBanner && <PromoBanner />}
    </VStack>
  );
};

export default SecondarySidebar;
